/**
 * @fileoverview Component for a Checkbox-like list of Cards. Cards are uniquely identified by
 * their value property, and any number can be selected at once.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */
import {Card} from '/ui/components/card/card';
import {ClickableDiv} from '/ui/components/clickable/clickablediv';
import {KeyboardAccessibleList} from '/ui/components/keyboardaccessiblelist/keyboardaccessiblelist';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/selectfeatures/selectfeatures.module.scss';

/**
 * @param {{
 *   selected: !Array<number>,
 *   onSelected: function(Array<number>): void,
 *   children?: ?,
 * }} props
 * @returns {React.ReactNode}
 */
export function SelectableCardList({
  selected,
  onSelected,
  children,
}) {
  return (
    <KeyboardAccessibleList
      classList={[styles.cardList]}
      itemSelector={styles.cardContent}
      focusTargetSelector={styles.clickableCard}
    >
      {React.Children.map(children, child => {
        if (child?.type !== CardBody) {
          return child;
        }

        const value = child.props.value;
        const isSelected = selected.includes(value);

        const card = (
          <Card key={value} selected={isSelected} taskFlowStyle>
            {child}
          </Card>
        );

        if (child.props.disabled) {
          return (
            <li>{card}</li>
          );
        } else {
          const onClick = isSelected
            ? () => onSelected(selected.filter(s => s !== value))
            : () => onSelected([...selected, value]);

          return (
            <li>
              <ClickableDiv className={styles.clickableCard} onClick={onClick}>
                {card}
              </ClickableDiv>
            </li>
          );
        }
      })}
    </KeyboardAccessibleList>
  );
}

/**
 * @param {{
 *   title: string,
 *   value: number,
 *   disabled: (boolean | undefined),
 *   children: (React.ReactNode | undefined),
 * }} props
 * @return {React.ReactNode}
 */
function CardBody({
  title,
  value,
  disabled,
  children,
}) {
  return (
    <Card.Body>
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          {title}
        </div>

        {children}
      </div>
    </Card.Body>
  );
}

SelectableCardList.Card = CardBody;
